import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const electricViolet: TemplateRegistration = {
  meta: {
    id: 'electric-violet',
    name: 'Electric Violet',
    category: 'Modern',
    description: 'Bold violet and lime with wide single-column impact',
    previewImage: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Space Grotesk', 'DM Sans']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="content-col">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="content-col">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-dark">
        <div class="content-col">
          <h2 class="section-title"><span class="neon-underline">About Us</span></h2>
          <p class="about-text">${safeAbout}</p>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceBars = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-bar">
            <div class="service-bar-icon">${getIconSvg(s.icon || 'star')}</div>
            <div class="service-bar-content">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
            </div>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-darker">
        <div class="content-col">
          <h2 class="section-title"><span class="neon-underline">Our Services</span></h2>
          <div class="services-stack">${serviceBars}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-dark">
        <div class="content-col">
          <h2 class="section-title"><span class="neon-underline">Gallery</span></h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-darker">
        <div class="content-col">
          <h2 class="section-title"><span class="neon-underline">What People Say</span></h2>
          <div class="testimonials-stack">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-dark">
        <div class="content-col">
          <h2 class="section-title"><span class="neon-underline">FAQ</span></h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-darker">
        <div class="content-col">
          <h2 class="section-title"><span class="neon-underline">Pricing</span></h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-dark">
        <div class="content-col">
          <h2 class="section-title"><span class="neon-underline">Hours</span></h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-darker">
        <div class="content-col">
          <h2 class="section-title"><span class="neon-underline">Get In Touch</span></h2>
          <div class="contact-stack">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #7C3AED; --bg: #0F0720; --bg-alt: #1A0F2E; --accent: #84CC16;
      --text: #F5F3FF; --text-muted: #A78BFA;
      --font-heading: 'Space Grotesk', sans-serif; --font-body: 'DM Sans', sans-serif;
      --glow-violet: 0 0 20px rgba(124,58,237,.4), 0 0 40px rgba(124,58,237,.15);
      --glow-lime: 0 0 20px rgba(132,204,22,.4), 0 0 40px rgba(132,204,22,.15);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .content-col { max-width: 900px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NEON UNDERLINE */
    .neon-underline { display: inline; background-image: linear-gradient(var(--primary), var(--primary)); background-size: 100% 3px; background-repeat: no-repeat; background-position: bottom; padding-bottom: 6px; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(15,7,32,.92); backdrop-filter: blur(10px); border-bottom: 1px solid rgba(124,58,237,.3); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 900px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.4rem; color: var(--text); font-weight: 700; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 500; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 3px; background: var(--primary); margin: 5px 0; transition: .3s; border-radius: 2px; }

    /* HERO */
    @keyframes heroGradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center; overflow: hidden; background: linear-gradient(135deg, #0F0720, #1A0F2E, #2D1B69, #1A0F2E, #0F0720); background-size: 400% 400%; animation: heroGradientShift 10s ease infinite; }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .1; }
    .hero-grid { position: absolute; inset: 0; background-image: linear-gradient(rgba(124,58,237,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,.08) 1px, transparent 1px); background-size: 60px 60px; background-position: center center; z-index: 1; }
    .hero-overlay { position: absolute; inset: 0; background: radial-gradient(ellipse at center, transparent 0%, rgba(15,7,32,.5) 70%, rgba(15,7,32,.85) 100%); z-index: 1; }
    .hero-content { position: relative; z-index: 2; padding: 120px 24px 80px; max-width: 900px; margin: 0 auto; width: 100%; text-align: center; display: flex; flex-direction: column; align-items: center; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(4rem, 10vw, 8rem); line-height: 1.05; margin-bottom: 24px; font-weight: 700; }
    .hero h1 .violet-glow { color: var(--text); text-shadow: 0 0 10px rgba(124,58,237,.8), 0 0 30px rgba(124,58,237,.6), 0 0 60px rgba(124,58,237,.4), 0 0 100px rgba(124,58,237,.2); }
    .hero-divider { width: 120px; height: 2px; border: none; background: var(--primary); margin: 0 auto 28px; box-shadow: 0 0 8px rgba(124,58,237,.8), 0 0 20px rgba(124,58,237,.5), 0 0 40px rgba(124,58,237,.3); }
    .hero-sub { font-size: clamp(1rem, 2.5vw, 1.25rem); color: var(--text-muted); max-width: 600px; margin-bottom: 40px; text-align: center; }
    .hero-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: var(--primary); color: var(--text); font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: none; border-radius: 6px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: var(--glow-violet); }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 0 30px rgba(124,58,237,.6), 0 0 60px rgba(124,58,237,.3); opacity: 1; color: var(--text); }
    .hero-cta-ghost { display: inline-block; padding: 16px 40px; background: transparent; color: var(--text); font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: 2px solid var(--primary); border-radius: 6px; cursor: pointer; transition: transform .2s, box-shadow .2s, background .2s; }
    .hero-cta-ghost:hover { transform: translateY(-2px); background: rgba(124,58,237,.15); box-shadow: var(--glow-violet); opacity: 1; color: var(--text); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-dark { background: var(--bg); }
    .section-darker { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.5rem); font-weight: 700; margin-bottom: 40px; }

    /* ABOUT */
    .about-text { font-size: 1.1rem; color: var(--text-muted); line-height: 1.9; }

    /* SERVICES - horizontal bars */
    .services-stack { display: flex; flex-direction: column; gap: 16px; }
    .service-bar { display: flex; align-items: center; gap: 20px; padding: 24px; background: rgba(124,58,237,.08); border: 1px solid rgba(124,58,237,.2); border-radius: 10px; transition: border-color .3s, box-shadow .3s; }
    .service-bar:hover { border-color: var(--primary); box-shadow: var(--glow-violet); }
    .service-bar-icon { flex-shrink: 0; color: var(--accent); }
    .icon-char { font-size: 28px; line-height: 1; }
    .service-bar-content { flex: 1; }
    .service-bar-content h3 { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 700; margin-bottom: 4px; }
    .service-bar-content p { color: var(--text-muted); font-size: .9rem; line-height: 1.5; }
    .service-price { flex-shrink: 0; color: var(--accent); font-weight: 700; font-size: 1.1rem; font-family: var(--font-heading); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border-radius: 8px; border: 1px solid rgba(124,58,237,.2); transition: box-shadow .3s, transform .3s; }
    .gallery-item img:hover { box-shadow: var(--glow-violet); transform: scale(1.03); }

    /* TESTIMONIALS */
    .testimonials-stack { display: flex; flex-direction: column; gap: 24px; }
    .testimonial-card { padding: 28px; background: rgba(124,58,237,.06); border: 1px solid rgba(124,58,237,.15); border-radius: 10px; border-left: 4px solid var(--primary); }
    .testimonial-body p { font-size: 1.05rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #4C3775; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { display: flex; flex-direction: column; gap: 0; }
    .faq-item { border-bottom: 1px solid rgba(124,58,237,.2); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 700; margin-bottom: 10px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { display: flex; flex-direction: column; gap: 0; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid rgba(124,58,237,.2); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.5rem; color: var(--accent); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid rgba(124,58,237,.15); color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--primary); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg); font-size: 1.2rem; }
    .review-text { color: var(--bg); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: var(--bg-alt); padding: 20px 0; border-bottom: 1px solid rgba(124,58,237,.2); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: var(--bg); font-family: var(--font-heading); padding: 8px 20px; font-size: 1rem; font-weight: 700; border-radius: 6px; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT */
    .contact-stack { display: flex; flex-direction: column; gap: 32px; }
    .contact-info { display: flex; flex-direction: column; gap: 16px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: rgba(124,58,237,.08); border: 1px solid rgba(124,58,237,.25); border-radius: 8px; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s, box-shadow .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--primary); box-shadow: var(--glow-violet); }
    .contact-form button { padding: 16px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: var(--glow-lime); }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 0 30px rgba(132,204,22,.5), 0 0 60px rgba(132,204,22,.2); }

    /* FOOTER */
    .footer { background: var(--bg-alt); border-top: 1px solid rgba(124,58,237,.2); padding: 40px 0; text-align: center; }
    .footer p { color: var(--text-muted); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(15,7,32,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .service-bar { flex-direction: column; align-items: flex-start; text-align: left; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
      .hero-buttons { flex-direction: column; align-items: center; }
      .hero-cta, .hero-cta-ghost { width: 100%; text-align: center; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    ${heroImage ? `<div class="hero-bg" style="background-image:url('${escapeAttr(heroImage)}')"></div>` : ''}
    <div class="hero-grid"></div>
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <h1><span class="violet-glow">${safeHeadline}</span></h1>
      <hr class="hero-divider">
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      <div class="hero-buttons">
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
        ${enabledFeatures.has('services-list') && data.services.length > 0 ? `<a href="#services" class="hero-cta-ghost">Our Services</a>` : enabledFeatures.has('about-section') ? `<a href="#about" class="hero-cta-ghost">Learn More</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="content-col">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
